import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_COMPACT_CONTROL_CLASS, FORM_HELP_TEXT_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import { glossaryFromCsv, type GlossaryTerm } from "./artifactTables";
import WordingsInput from "./WordingsInput";

interface GlossaryTableProps {
  rows: readonly GlossaryTerm[];
  /** The label list's keys, or `null` when none is chosen: the label is then typed. */
  labelKeys: readonly string[] | null;
  onChange: (rows: readonly GlossaryTerm[]) => void;
}

const EMPTY_TERM: GlossaryTerm = { key: "", label: "", de: [], en: [], note: "" };

/** `dmw-term-glossary/1` as a table (AU-5): key, label, German and English wordings, note. */
export default function GlossaryTable({ rows, labelKeys, onChange }: GlossaryTableProps) {
  const { t } = useTranslation();
  const fileId = useId();
  const [importProblem, setImportProblem] = useState(false);
  const replace = (index: number, row: GlossaryTerm | null) => {
    onChange(rows.flatMap((other, position) => (position !== index ? [other] : row === null ? [] : [row])));
  };

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <caption className="sr-only">{t("authoring.artifact.glossary.caption")}</caption>
          <thead>
            <tr className="app-text-muted text-left">
              <th className="px-1 py-1">{t("authoring.artifact.glossary.key")}</th>
              <th className="px-1 py-1">{t("authoring.artifact.glossary.label")}</th>
              <th className="px-1 py-1">{t("authoring.artifact.glossary.de")}</th>
              <th className="px-1 py-1">{t("authoring.artifact.glossary.en")}</th>
              <th className="px-1 py-1">{t("authoring.artifact.glossary.note")}</th>
              <th className="px-1 py-1"><span className="sr-only">{t("authoring.artifact.rowActions")}</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const name = row.key === "" ? String(index + 1) : row.key;
              return (
                <tr key={index} className="align-top">
                  <td className="px-1 py-0.5">
                    <input className={`${FORM_COMPACT_CONTROL_CLASS} font-mono`} aria-label={t("authoring.artifact.glossary.keyOf", { row: index + 1 })} value={row.key} onChange={(event) => { replace(index, { ...row, key: event.target.value }); }} />
                  </td>
                  <td className="px-1 py-0.5">
                    {labelKeys === null ? (
                      <input className={`${FORM_COMPACT_CONTROL_CLASS} font-mono`} aria-label={t("authoring.artifact.glossary.labelOf", { name })} value={row.label} onChange={(event) => { replace(index, { ...row, label: event.target.value }); }} />
                    ) : (
                      <select className={FORM_SELECT_CLASS} aria-label={t("authoring.artifact.glossary.labelOf", { name })} value={row.label} onChange={(event) => { replace(index, { ...row, label: event.target.value }); }}>
                        <option value="">{t("authoring.artifact.glossary.noLabel")}</option>
                        {labelKeys.includes(row.label) || row.label === "" ? null : <option value={row.label}>{row.label}</option>}
                        {labelKeys.map((key) => <option key={key} value={key}>{key}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="px-1 py-0.5">
                    <WordingsInput label={t("authoring.artifact.glossary.deOf", { name })} wordings={row.de} onChange={(de) => { replace(index, { ...row, de }); }} />
                  </td>
                  <td className="px-1 py-0.5">
                    <WordingsInput label={t("authoring.artifact.glossary.enOf", { name })} wordings={row.en} onChange={(en) => { replace(index, { ...row, en }); }} />
                  </td>
                  <td className="px-1 py-0.5">
                    <input className={FORM_COMPACT_CONTROL_CLASS} aria-label={t("authoring.artifact.glossary.noteOf", { name })} value={row.note} onChange={(event) => { replace(index, { ...row, note: event.target.value }); }} />
                  </td>
                  <td className="px-1 py-0.5">
                    <AppButton variant="ghost" size="xs" aria-label={t("authoring.artifact.removeRowNamed", { name })} onClick={() => { replace(index, null); }}>
                      {t("authoring.artifact.removeRow")}
                    </AppButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <AppButton variant="secondary" size="xs" onClick={() => { onChange([...rows, EMPTY_TERM]); }}>{t("authoring.artifact.glossary.addTerm")}</AppButton>
        <label htmlFor={fileId} className="app-button-secondary app-button-xs cursor-pointer">{t("authoring.artifact.glossary.importCsv")}</label>
        <input
          id={fileId}
          type="file"
          accept=".csv,text/csv"
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file !== undefined) {
              void file.text().then((csv) => {
                const imported = glossaryFromCsv(csv);
                setImportProblem(imported === null);
                if (imported !== null) {
                  onChange([...rows, ...imported]);
                }
              });
            }
          }}
        />
      </div>
      {importProblem ? <p className="app-error-text text-xs">{t("authoring.artifact.glossary.importProblem")}</p> : null}
      <p className={FORM_HELP_TEXT_CLASS}>{t("authoring.artifact.glossary.hint")}</p>
    </div>
  );
}
